<template>
  <!-- Auth screens render bare; the documentation gets its own shell —
       deliberately NOT the application one: se lee con el ERP abierto en la
       pestaña de al lado, y dos menús iguales en las dos pestañas son dos
       pestañas que se confunden. Todo lo demás, el armazón de siempre. -->
  <DocsLayout v-if="isDocsRoute">
    <RouterView />
  </DocsLayout>
  <AppLayout v-else-if="!isAuthRoute">
    <RouterView />
  </AppLayout>
  <RouterView v-else />

  <UiToastHost />
  <UiConfirmHost />
  <UpdateBanner />

  <!-- Available on every screen, because a question about what you are looking
       at should not require navigating away from it. -->
  <AssistantPanel v-if="auth.isAuthenticated" />

  <!-- Lo que un módulo necesita en todas las pantallas —una llamada que suena
       mientras se está en una factura—. Ver `moduleOverlays` en registry.js. -->
  <template v-if="auth.isAuthenticated && !isAuthRoute">
    <component :is="overlay.component" v-for="overlay in overlays" :key="`${overlay.module}:${overlay.id}`" />
  </template>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  UiToastHost, UiConfirmHost, useToast, configureLabels,
  adoptThemePreference, storedThemePreference,
} from '@general/ui-kit'
import { useAuthStore, onBeforeLogout } from '@general/auth/frontend'
import { useCompanyStore, brandName, applyBrand, loadBrand } from '@general/companies/frontend'
import { useNotificationStore, releasePushOnSignOut } from '@general/notifications/frontend'

import { AssistantPanel, setGlobalContext } from '@general/assistant/frontend'

import { realtime } from '@general/realtime/frontend'

import { visibleNavigation, moduleForPath } from './app/navigation'
import { moduleOverlays } from './app/registry'
import { mountedModules } from './app/instance'
import { allows, passwordChangeDetour } from './app/access'
import AppLayout from './layouts/AppLayout.vue'
import DocsLayout from './layouts/DocsLayout.vue'
import UpdateBanner from './components/UpdateBanner.vue'
import { STORAGE_KEY, toCode } from './app/languages'
import { adoptAccountPreferences, preferenceSaver } from './app/accountPreferences'
import { isDemoInstance } from '@general/demo/frontend'

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const auth = useAuthStore()
const company = useCompanyStore()
const notifications = useNotificationStore()
const toast = useToast()

// Cerrar sesión da de baja el push de este navegador antes de revocar el
// token: en un aparato compartido, los avisos del que sale no pueden seguir
// sonando con el siguiente dentro.
onBeforeLogout(() => releasePushOnSignOut())

// Until the first navigation resolves, `route` is the router's empty start and
// says nothing about the layout: the application shell — menu, logo, «General
// ERP» — was painted for a moment on top of the sign-in screen at every load
// of /login. The address bar already knows where we are; its route record
// carries the layout, even while the screen's code is still downloading.
const routed = ref(false)
router.isReady().then(() => { routed.value = true }, () => { routed.value = true })
const firstMeta = (() => {
  try { return router.resolve(router.options.history.location).meta ?? {} } catch { return {} }
})()
const layout = computed(() => (routed.value ? route.meta.layout : firstMeta.layout))
const isAuthRoute = computed(() => layout.value === 'auth')
const isDocsRoute = computed(() => layout.value === 'docs')

// Los módulos que corren de verdad aquí (`/health`). `null` mientras no se sabe
// o si no se pudo saber: entonces solo decide el permiso, como antes.
const mounted = ref(null)
mountedModules().then(answer => { mounted.value = answer })
const overlays = computed(() => moduleOverlays.filter(overlay =>
  allows(overlay.permission, permission => auth.can(permission))
  && (!overlay.module || !Array.isArray(mounted.value) || mounted.value.includes(overlay.module))))

/** Everything that has to happen once there is a session. */
async function startSession() {
  // The person's own theme and language, as their ACCOUNT has them — chosen on
  // another device, maybe. Settled before the company default below, which only
  // speaks for someone who never chose. The two requests go together.
  const [profile] = await Promise.all([company.load(), syncAccountPreferences()])

  // The company profile sets the instance default, but a person who picked a
  // language in the top bar keeps it: their choice outranks the default.
  if (profile?.locale && !localStorage.getItem(STORAGE_KEY)) {
    locale.value = toCode(profile.locale)
    document.documentElement.lang = locale.value
  }

  notifications.fetchFeed()
  notifications.connect()
}

// Asks the account for a fresh profile and applies its theme and language. The
// cached profile in the browser is from the last visit on THIS device, which is
// exactly what does not know about a choice made on the other one.
let preferencesCheckedAt = 0
async function syncAccountPreferences() {
  // Inside the customer portal the portal account decides (PortalShell), even
  // when a staff member happens to be signed into the ERP in the same browser.
  if (window.location.pathname.includes('/portal')) return
  preferencesCheckedAt = Date.now()
  await auth.refreshProfile()
  await adoptAccountPreferences(auth.user, {
    locale,
    save: preferenceSaver(auth.savePreferences, isDemoInstance),
    theme: { adopt: adoptThemePreference, stored: storedThemePreference },
  })
}

// An installed app on a phone is not reloaded for days: it sleeps and wakes.
// On waking it asks again, at most every few minutes, so a theme changed on the
// laptop in the meantime reaches it without closing anything.
const PREFERENCES_RECHECK_MS = 5 * 60 * 1000
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible' || !auth.isAuthenticated) return
  if (Date.now() - preferencesCheckedAt < PREFERENCES_RECHECK_MS) return
  syncAccountPreferences()
})

// Whether the current session's loss has already been announced and routed.
let sessionLossHandled = false

onMounted(() => {
  // The client's name and icon, signed in or not: the sign-in screen is the
  // client's door, and the tab, the favicon and the iOS home-screen name are
  // read from this page before anybody has a session. The last copy first,
  // so the door is not the product's blue while the request is in flight.
  applyBrand()
  loadBrand()

  if (auth.isAuthenticated) startSession()

  // The API client clears the tokens and fires this when a refresh fails. The
  // shell is the only place that knows how to route, so it handles the redirect.
  //
  // Once per lost session, not once per request. Every call that was in flight
  // when the session died fails on its own and fires this — opening a screen
  // is five or seven of them at once — and each used to leave its own «your
  // session has expired» on top of the last. The flag is re-armed on the next
  // sign-in (see the watch below).
  window.addEventListener('general:unauthorized', () => {
    if (sessionLossHandled) return
    sessionLossHandled = true
    notifications.reset()
    toast.warning(t('auth.sessionExpired'))
    if (!route.path.startsWith('/login')) {
      router.push({ name: 'login', query: { redirect: route.fullPath } })
    }
  })

  // The stream carries the token in its URL, so it dies with the old one and
  // has to be rebuilt after every silent refresh. It is the shared one now
  // (platform/realtime): reopening it here reopens it for the notifications,
  // the mail client and the conversations at once, and each of them catches up
  // on its own when it opens again.
  window.addEventListener('general:token-refreshed', () => {
    realtime.reconnect()
  })

  // Shared device: nothing from the previous user stays in memory.
  // Si la sesión la cerró OTRA pestaña (`elsewhere`), aquí nadie ha llevado a
  // la persona a la entrada: los datos ya se vaciaron, falta la pantalla.
  window.addEventListener('general:session-ended', (event) => {
    notifications.reset()
    if (event.detail?.elsewhere && !route.meta?.public) {
      router.push({ name: 'login', query: { redirect: route.fullPath } })
    }
  })
})

// The profile can learn it later than the router: a session opened before the
// flag was enforced, or on waking, when `/me` answers. Then the screen already
// drawn is the empty one, so move to where the password is changed.
watch(() => auth.user?.mustChangePassword, () => {
  const detour = passwordChangeDetour(auth.user, route)
  if (detour) router.replace(detour)
})

// Signing in from the login screen has to bring the session to life too.
watch(() => auth.isAuthenticated, (signedIn) => {
  if (signedIn) {
    // A new session can be lost again, and that one deserves its warning.
    sessionLossHandled = false
    startSession()
  } else notifications.reset()
})

// The words the design system says on its own behalf: the label on a close
// cross, the one a screen reader hears over a column of buttons. The ui-kit
// holds no translations of its own, so the shell hands them over — here and
// again on every language change, or a switch to French would leave the
// invisible half of the interface in the previous language.
watch(locale, () => {
  configureLabels({
    close: t('common.close'),
    dismiss: t('common.dismiss'),
    cancel: t('common.cancel'),
    confirm: t('common.confirm'),
    actions: t('common.actions'),
    loading: t('common.loading'),
    loadFailed: t('common.loadFailed'),
    retry: t('common.retry'),
    empty: t('common.nothingYet'),
    showPassword: t('auth.showPassword'),
    hidePassword: t('auth.hidePassword'),
    filters: t('common.filters'),
    summary: t('common.summary'),
    filterOptions: t('common.filterOptions'),
    noOptions: t('common.noOptions'),
    guideShow: t('common.guideShow'),
    guideHide: t('common.guideHide'),
    tutorialOpen: t('common.tutorialOpen'),
    tutorialShort: t('common.tutorialShort'),
    audioRecord: t('common.audioRecord'),
    audioStop: t('common.audioStop'),
    audioRetry: t('common.audioRetry'),
    audioPlay: t('common.audioPlay'),
    audioPause: t('common.audioPause'),
    audioDelete: t('common.audioDelete'),
    audioDenied: t('common.audioDenied'),
    audioMissing: t('common.audioMissing'),
    audioBusy: t('common.audioBusy'),
    audioInsecure: t('common.audioInsecure'),
    audioUnsupported: t('common.audioUnsupported'),
    cameraDenied: t('common.cameraDenied'),
    cameraMissing: t('common.cameraMissing'),
    cameraBusy: t('common.cameraBusy'),
    cameraInsecure: t('common.cameraInsecure'),
    cameraUnsupported: t('common.cameraUnsupported'),
    selectAll: t('common.selectAll'),
    mediaPhoto: t('common.mediaPhoto'),
    mediaVideo: t('common.mediaVideo'),
    mediaGallery: t('common.mediaGallery'),
    mediaRemove: t('common.remove'),
    mediaFull: t('common.mediaFull'),
    mediaTooBig: t('common.mediaTooBig'),
    viewerTitle: t('common.viewerTitle'),
    viewerPrevious: t('common.viewerPrevious'),
    viewerNext: t('common.viewerNext'),
    viewerDownload: t('common.viewerDownload'),
    tagPlaceholder: t('common.tagPlaceholder'),
    tagMore: t('common.tagMore'),
    tagRemove: t('common.remove'),
    tagReorder: t('common.tagReorder'),
    tagSuggestions: t('common.tagSuggestions'),
    tagUnknown: t('common.tagUnknown'),
    tagRejected: t('common.tagRejected'),
    tagFull: t('common.tagFull'),
  })
}, { immediate: true })

// Keep the tab title meaningful: it is how people find the right window among
// fifteen of them.
//
// The name is the installed app's (the brand), so the tab and the app's window
// say the same thing; it updates the moment the brand arrives or changes.
watch([() => route.meta.title, brandName], ([title, name]) => {
  document.title = title ? `${t(title)} · ${name}` : name
}, { immediate: true })

/**
 * Every page this person can actually open.
 *
 * "Where do I do that?" is half of what anybody asks an assistant built into an
 * application, and the assistant had no way to answer it: the backend knows
 * which modules are installed and nothing about what their screens are called,
 * because routes are discovered from the filesystem when the SPA is built.
 *
 * The list is the sidebar's own — same entries, same permission filter — so a
 * screen the menu hides from this person is a screen the assistant will not
 * send them to. Translated here because the titles are i18n keys, and it is
 * their language the answer has to be in.
 */
const pages = computed(() => visibleNavigation(auth.can).flatMap(group =>
  group.items.flatMap(item => [
    // `module` viaja con cada página para que el backend mande solo las del
    // alcance del turno. Noventa entradas eran 970 tokens en CADA llamada — el
    // 10% de la factura — para contestar una pregunta de cada veinte.
    { title: t(item.labelKey), path: item.to, module: item.module ?? null },
    ...(item.children ?? []).map(child => ({
      title: t(child.labelKey), path: child.to, module: item.module ?? null,
    })),
  ])))

// The shell's contribution to the assistant's screen context: where the user
// is, and what else there is. Modules add the detail of what is on that screen
// from their own views.
// Watched together with the route, not only after it: the assistant can now
// change what somebody may do, and the page it just opened for them has to be
// in the next question's context without waiting for them to navigate first.
watch([() => route.fullPath, pages], () => {
  setGlobalContext({
    route: route.path,
    screen: route.meta.title ? t(route.meta.title) : null,
    language: locale.value,
    pages: pages.value,
    // Which module owns this screen. The backend uses it to decide what the
    // assistant carries into the turn: somebody looking at an invoice gets the
    // invoicing tools whatever they type. Only the SPA knows this — the routes
    // are discovered from the filesystem, not declared in any manifest.
    module: moduleForPath(route.path),
  })
}, { immediate: true })
</script>
